"use client";

import { usePathname, useRouter } from "next/navigation";
import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useMenu } from "@/lib/menu/MenuProvider";
import { MenuItem } from "@/lib/menu/MenuItem";
import { SearchPanel, reportDef } from "@/lib/menu/dialogs";
import SettingsContent, {
  menu as settingsMenu,
} from "@/lib/menu/content/settings.mdx";
import type { MenuDef } from "@/lib/menu/menus";
import { hideTip, showTip } from "@/lib/menu/Tooltip";
import { useToast } from "@/lib/toast";
import type { ChatKind } from "./ChatShell";
import { NotesList } from "./NotesList";
import { copyText } from "@/lib/clipboard";
import { BADGES, type BadgeKey } from "./TierBadges";
import {
  DMS,
  MEMBERS,
  STATUS_TEXT,
  memberUuid,
  type Member,
} from "./data";
import {
  CheckIcon,
  CopyIcon,
  CrownIcon,
  GearIcon,
  KeyIcon,
  LinkIcon,
  MessageIcon,
  PlusIcon,
  SearchIcon,
  SendIcon,
  ShareIcon,
  ShieldIcon,
  UserCheckIcon,
  UserPlusIcon,
  UsersIcon,
  XIcon,
} from "./icons";
import { Pfp } from "./Pfp";
import { IconButton, SectionLabel } from "./ui";

/* honeycomb rows — 3 / 4 / 3 staggered cluster */
const HONEY = [
  MEMBERS.slice(0, 3),
  MEMBERS.slice(3, 7),
  MEMBERS.slice(7),
];

/* GC rank logic — only the owner's crown renders, tilted on the pfp's
   top-right corner; mod/perms keep their data and tips but no icon. */
const RANK: Record<
  NonNullable<Member["rank"]>,
  { Icon: typeof CrownIcon; color: string; tip: string } | null
> = {
  owner: { Icon: CrownIcon, color: "text-idle", tip: "Owner · 1/1, required" },
  followup: null,
  mod: { Icon: ShieldIcon, color: "text-accent", tip: "Mod · 3/5" },
  perms: { Icon: KeyIcon, color: "text-work", tip: "Perms · 3/25" },
};

/* the only badge types that display on a user/profile — both bug
   bounties and staff. joinday/first100/founder don't display. */
/* row-strip badges — everything except the implied milestones */
const DISPLAY_BADGES = Object.keys(BADGES).filter(
  (k): k is BadgeKey => k !== "joinday" && k !== "first100",
);

/* badge strip — display badges only */
export function MemberBadges({ m, size = 11 }: { m: Member; size?: number }) {
  const keys = (m.badges ?? []).filter((b) => DISPLAY_BADGES.includes(b));
  if (!keys.length) return null;
  return (
    <span className="flex shrink-0 items-center gap-1">
      {keys.map((k) => {
        const B = BADGES[k];
        return (
          <span
            key={k}
            title={B.label}
            style={{ width: size, height: size }}
            className="block shrink-0"
            onPointerEnter={(e) =>
              showTip(B.label, e.currentTarget, "top")
            }
            onPointerLeave={hideTip}
          >
            <B.Icon className="h-full w-full" />
          </span>
        );
      })}
    </span>
  );
}

/* GC rank mark — sits on the pfp's top-right corner, tilted like the
   member rows; only the owner's crown renders there */
function RankMark({ m }: { m: Member }) {
  const rank = m.rank ? RANK[m.rank] : null;
  if (!rank || m.rank !== "owner") return null;
  return (
    <span
      className="absolute -right-2 -top-2"
      onPointerEnter={(e) => showTip(rank.tip, e.currentTarget, "top")}
      onPointerLeave={hideTip}
    >
      <CrownIcon
        aria-label={rank.tip}
        className="h-6 w-6 rotate-[35deg] text-idle drop-shadow-[0_1px_1px_rgb(0_0_0/0.7)]"
      />
    </span>
  );
}

/* copy "id#tag" (or any line) to the clipboard with a toast */
function useCopyLine() {
  const toast = useToast();
  return (v: string) => () => {
    copyText(v);
    toast.top("Copied", CopyIcon);
  };
}

/* tap-to-copy handle — hovering slides the copy chip open inline */
function CopyHandle({ m }: { m: Member }) {
  const copyLine = useCopyLine();
  const handle = `${m.id}#${m.tag}`;
  return (
    <span className="group/handle flex w-fit min-w-0 items-center gap-1.5">
      <button
        type="button"
        onClick={copyLine(handle)}
        className="truncate text-title text-fg-faint transition hover:text-fg-mute"
      >
        {handle}
      </button>
      <button
        type="button"
        aria-label="Copy username"
        onClick={copyLine(handle)}
        className="grid w-0 place-items-center overflow-hidden text-fg-faint opacity-0 transition-all duration-150 group-hover/handle:w-4.5 group-hover/handle:opacity-100 hover:text-fg"
      >
        <CopyIcon className="h-3.5 w-3.5" />
      </button>
    </span>
  );
}

/* the bio is plain text, not a card — `onPress` (quick card) escalates
   to the full profile; without it the bio is inert (it already lives in
   the big profile) */
function ProfileBio({ m, onPress }: { m: Member; onPress?: () => void }) {
  const text = m.bio ?? "This user has no bio set";
  const cls = `text-left text-title ${m.bio ? "text-fg-mute" : "text-fg-faint"}`;
  return onPress ? (
    <button
      type="button"
      onClick={onPress}
      className={`${cls} rounded-sm transition hover:text-fg-mute`}
    >
      {text}
    </button>
  ) : (
    <p className={cls}>{text}</p>
  );
}

/* mutual servers/friends rows — the faces are display-only; the text
   label is the press target and opens the profile owner's big profile
   (onTextPress). In the big profile itself it's inert — already there */
function ProfileMutuals({
  m,
  onTextPress,
}: {
  m: Member;
  onTextPress?: () => void;
}) {
  const muts = (m.mutFriends ?? [])
    .map((id) => MEMBERS.find((x) => x.id === id))
    .filter((x): x is Member => !!x);
  const servers = m.mutServers ?? [];
  if (!servers.length && !muts.length) return null;
  const label = (text: string) =>
    onTextPress ? (
      <button
        type="button"
        onClick={onTextPress}
        className="min-w-0 truncate rounded-sm transition hover:text-fg-mute"
      >
        {text}
      </button>
    ) : (
      <span className="min-w-0 truncate">{text}</span>
    );
  return (
    <>
      <span aria-hidden className="mx-1 block h-px bg-line" />
      <div className="flex flex-col gap-1.5">
        {servers.length ? (
          <div className="flex items-center gap-2 text-micro text-fg-dim">
            <FaceStack
              items={servers.map((s) => ({ src: s.src, label: s.name }))}
            />
            {label(`mutual servers (${servers.length})`)}
          </div>
        ) : null}
        {muts.length ? (
          <div className="flex items-center gap-2 text-micro text-fg-dim">
            <FaceStack
              items={muts.map((x) => ({ src: x.src, label: x.name }))}
            />
            {label(`mutual friends (${muts.length})`)}
          </div>
        ) : null}
      </div>
    </>
  );
}

/* card-footer action — DM row for friends, add otherwise. Full-bleed:
   the button IS the card's bottom edge, so no card chrome hangs below
   it; the card's overflow clips its bottom corners to the card radius */
function FriendAction({ m }: { m: Member }) {
  const router = useRouter();
  const toast = useToast();
  const cls =
    "flex w-full items-center justify-center gap-2 bg-accent px-4 py-3 text-note font-semibold text-fg transition hover:brightness-110";
  return m.friended ? (
    <button
      type="button"
      onClick={() => router.push(`/app/d/${m.id}`)}
      className={cls}
    >
      <MessageIcon className="h-4 w-4" />
      Open DMs
    </button>
  ) : (
    <button
      type="button"
      onClick={() => toast.top("Friend request sent", PlusIcon)}
      className={cls}
    >
      <PlusIcon className="h-4 w-4" />
      Add friend
    </button>
  );
}

/* the partial profile card — core info only, rides as a bare mobileCard
   on mobile and a cursor dropdown on desktop */
function MemberCard({ m }: { m: Member }) {
  const s = STATUS_TEXT[m.status];
  return (
    <div className="w-56 p-3.5">
      <Pfp src={m.src} status={m.status} size={56} rounded="rounded-xl" />
      <div className="mt-2.5 flex items-center gap-1.5">
        <p className="truncate text-note font-bold text-fg">{m.name}</p>
        <MemberBadges m={m} />
      </div>
      <p className="text-micro text-fg-faint">
        {m.id}#{m.tag}
        {m.role ? ` · ${m.role}` : ""}
      </p>
      <p className={`mt-2 text-micro font-semibold ${s.color}`}>{s.label}</p>
    </div>
  );
}

export const memberDef = (m: Member): MenuDef => ({
  id: `member-${m.id}`,
  title: m.name,
  Content: () => <MemberCard m={m} />,
  mobileCard: true,
  bare: true,
  bottomCard: true,
});

/* mid-size profile popup — the "big profile" every surface escalates to.
   The three profile sizes are: sm = the member card above, mid = this
   popup, lg = a page (coming soon). On mobile it's also what a member-row
   tap opens directly, so the display name copies the handle there instead
   of re-opening the popup it's already inside */
/* auto-grow — wraps instead of scrolling */
const growArea = (el: HTMLTextAreaElement) => {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

/* the mobile note editor — a stacked bottom card over the open profile
   sheet (the menu's `sub` slot): autofocused textarea, same 128-cap
   counter, Enter or the scrim sinks it back down */
function NoteEditor({
  initial,
  onChange,
}: {
  initial: string;
  onChange: (v: string) => void;
}) {
  const [v, setV] = useState(initial);
  const { closeSub } = useMenu();
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    if (ref.current) growArea(ref.current);
  }, []);
  return (
    <div className="px-3 pb-4">
      <p className="flex items-baseline gap-1.5 text-note font-semibold text-fg-mute">
        Note
        <span className="font-normal text-fg-faint">
          (only visible to you)
        </span>
        {v.length >= 100 ? (
          <span
            aria-hidden
            className={`ml-auto text-[10px] font-normal tabular-nums ${
              v.length >= 128 ? "text-dnd" : "text-fg-faint"
            }`}
          >
            {v.length}/128
          </span>
        ) : null}
      </p>
      <textarea
        ref={ref}
        autoFocus
        rows={1}
        maxLength={128}
        wrap="soft"
        value={v}
        onChange={(e) => {
          setV(e.target.value);
          onChange(e.target.value);
          growArea(e.currentTarget);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            closeSub();
          }
        }}
        placeholder="Click to add a note"
        className="mt-1 w-full resize-none break-words overflow-hidden bg-transparent text-note italic text-fg-mute outline-none placeholder:text-fg-faint"
      />
    </div>
  );
}

const noteDef = (
  m: Member,
  note: string,
  onChange: (v: string) => void,
): MenuDef => ({
  id: `note-${m.id}`,
  title: `Note for ${m.name}`,
  Content: () => <NoteEditor initial={note} onChange={onChange} />,
  mobileCard: true,
  bare: true,
  bottomCard: true,
});

/* private note — desktop: an invisible inline text box that looks like
   the "Click to add a note" line until you click it and type. Mobile:
   that line pulls up the stacked note card over the sheet instead.
   128-char cap; the counter only wakes up past 100 and burns red at the
   limit. value lives in state only; persistence is a data-layer
   problem */
function ProfileNote({ m }: { m: Member }) {
  const [note, setNote] = useState(m.note ?? "");
  const { mobile, popup } = useMenu();
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useLayoutEffect(() => {
    if (!mobile && ref.current) growArea(ref.current);
  }, [mobile]);
  return (
    <>
      <p className="mt-5 flex items-baseline gap-1.5 text-note font-semibold text-fg-mute">
        Note
        <span className="font-normal text-fg-faint">
          (only visible to you)
        </span>
        {!mobile && note.length >= 100 ? (
          <span
            aria-hidden
            className={`ml-auto text-[10px] font-normal tabular-nums ${
              note.length >= 128 ? "text-dnd" : "text-fg-faint"
            }`}
          >
            {note.length}/128
          </span>
        ) : null}
      </p>
      {mobile ? (
        <button
          type="button"
          aria-label="Edit note"
          onClick={() => popup(noteDef(m, note, setNote))}
          className={`mt-0.5 w-full rounded-sm text-left text-note italic transition ${
            note ? "text-fg-dim" : "text-fg-faint"
          }`}
        >
          {note || "Click to add a note"}
        </button>
      ) : (
        <textarea
          ref={ref}
          rows={1}
          maxLength={128}
          wrap="soft"
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            growArea(e.currentTarget);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              e.currentTarget.blur();
            }
          }}
          placeholder="Click to add a note"
          className="mt-0.5 w-full resize-none break-words overflow-hidden bg-transparent text-note italic text-fg-dim outline-none placeholder:text-fg-faint focus:text-fg-mute"
        />
      )}
    </>
  );
}

/* share sheet — stacks over the profile via the menu's `sub` slot: a
   bottom pullup on mobile, a wide tall card on desktop. title, search,
   a selectable share-to list (friends + dms + channels), then the
   message preview, an optional note next to the link-copy button, and
   Send */
function ShareCard({ m }: { m: Member }) {
  const { closeSub } = useMenu();
  const toast = useToast();
  const [q, setQ] = useState("");
  const [msg, setMsg] = useState("");
  const [sel, setSel] = useState<ReadonlySet<string>>(new Set());
  /* the canonical link — the /users/<uuid>:<tag> route hands off to the
     friends route, which pops this profile on landing */
  const href = `/users/${memberUuid(m)}:${m.tag}`;
  const copy = () => {
    copyText(`${location.origin}${href}`);
    toast.top("Profile link copied", ShareIcon);
  };
  const toggle = (k: string) =>
    setSel((s) => {
      const n = new Set(s);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const needle = q.trim().toLowerCase();
  const hits = (name: string, extra = "") =>
    !needle || `${name}${extra}`.toLowerCase().includes(needle);
  const friends = MEMBERS.filter((x) => x.friended && hits(x.name, x.id));
  const dms = DMS.filter(
    (d) => d.href.startsWith("/app/d/") && hits(d.name, d.hover),
  );
  const channels = DMS.filter(
    (d) => !d.href.startsWith("/app/d/") && hits(d.name, d.hover),
  );
  const section = (label: string) => (
    <p className="px-2 pb-1 pt-2 text-micro font-semibold tracking-wide text-fg-faint uppercase">
      {label}
    </p>
  );
  /* a share-to row — face + name/sub on the left, select box on the
     right instead of a per-row share button */
  const target = (key: string, face: ReactNode, name: string, sub: string) => {
    const on = sel.has(key);
    return (
      <button
        key={key}
        type="button"
        role="checkbox"
        aria-checked={on}
        onClick={() => toggle(key)}
        className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition hover:bg-raise/60"
      >
        {face}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-note font-semibold text-fg">
            {name}
          </span>
          <span className="block truncate text-micro text-fg-faint">
            {sub}
          </span>
        </span>
        <span
          aria-hidden
          className={`grid h-4.5 w-4.5 shrink-0 place-items-center rounded-md ring-1 transition ${
            on ? "bg-accent text-fg ring-accent" : "text-transparent ring-line"
          }`}
        >
          <CheckIcon className="h-3 w-3" />
        </span>
      </button>
    );
  };
  const lastLine = (d: (typeof DMS)[number]) =>
    d.msg ? `${d.from}: ${d.msg}` : (d.note ?? d.hover ?? "");
  return (
    <div className="flex h-full max-h-[78dvh] flex-col">
      <p className="px-3 pt-1.5 text-heading font-bold text-fg">
        Share {m.name}&rsquo;s profile!
      </p>
      {/* search — filters every section of the share-to list */}
      <div className="px-3 pb-1 pt-2.5">
        <div className="flex items-center gap-2 rounded-lg bg-ink/60 px-2.5 py-1.5 ring-1 ring-line transition focus-within:ring-accent/60">
          <SearchIcon className="h-3.5 w-3.5 shrink-0 text-fg-faint" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            className="min-w-0 flex-1 bg-transparent text-note text-fg outline-none placeholder:text-fg-faint"
          />
        </div>
      </div>
      {/* the share-to list — checkboxes, not per-row send buttons */}
      <div className="min-h-0 flex-1 overflow-y-auto scroll-dark px-1.5 py-1">
        {friends.length ? (
          <>
            {section("Friends")}
            {friends.map((f) =>
              target(
                `f:${f.id}`,
                <Pfp src={f.src} status={f.status} size={32} rounded="rounded-full" />,
                f.name,
                `${f.id}#${f.tag}`,
              ),
            )}
          </>
        ) : null}
        {dms.length ? (
          <>
            {section("Direct messages")}
            {dms.map((d) =>
              target(
                `d:${d.id}`,
                <Pfp src={d.src} status={d.status} size={32} rounded="rounded-full" />,
                d.name,
                lastLine(d),
              ),
            )}
          </>
        ) : null}
        {channels.length ? (
          <>
            {section("Channels")}
            {channels.map((d) =>
              target(
                `c:${d.id}`,
                // eslint-disable-next-line @next/next/no-img-element -- channel tile
                <img src={d.src} alt="" className="h-8 w-8 shrink-0 rounded-lg object-cover" />,
                d.name,
                lastLine(d),
              ),
            )}
          </>
        ) : null}
        {friends.length + dms.length + channels.length ? null : (
          <p className="px-2 py-4 text-center text-micro text-fg-faint">
            Nothing matches that search.
          </p>
        )}
      </div>
      {/* message preview — what the share looks like when it lands */}
      <div className="shrink-0 border-t border-line p-2.5">
        <div className="overflow-hidden rounded-xl bg-ink/40 ring-1 ring-line">
          <div
            aria-hidden
            className="h-9"
            style={{
              background:
                "linear-gradient(115deg, var(--color-accent) 0%, var(--color-work) 100%)",
            }}
          />
          <div className="flex items-center gap-2.5 px-2.5 pb-2.5">
            <span className="-mt-4 rounded-lg bg-slab p-0.5 ring-1 ring-line">
              <Pfp src={m.src} status={m.status} size={36} rounded="rounded-full" />
            </span>
            <span className="min-w-0 flex-1 pt-1">
              <span className="block truncate text-note font-bold text-fg">
                {m.name}
              </span>
              <span className="block truncate text-micro text-fg-faint">
                {m.id}#{m.tag}
              </span>
            </span>
            <MemberBadges m={m} size={14} />
          </div>
        </div>
        {/* optional message + link copy */}
        <div className="mt-2 flex items-center gap-2">
          <input
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="Add a message"
            autoCapitalize="off"
            spellCheck={false}
            className="min-w-0 flex-1 rounded-lg bg-ink/60 px-2.5 py-2 text-note text-fg outline-none ring-1 ring-line transition placeholder:text-fg-faint focus:ring-accent/60"
          />
          <IconButton
            label="Copy link"
            tip="Copy link"
            onClick={copy}
            className="bg-ink/60 ring-1 ring-line"
          >
            <LinkIcon className="h-4 w-4" />
          </IconButton>
        </div>
        <button
          type="button"
          disabled={!sel.size}
          onClick={() => {
            toast.top(
              `Shared to ${sel.size} ${sel.size === 1 ? "chat" : "chats"}`,
              SendIcon,
            );
            closeSub();
          }}
          className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2 text-note font-semibold text-fg transition enabled:hover:brightness-110 disabled:opacity-40"
        >
          <SendIcon className="h-4 w-4" />
          Send
        </button>
      </div>
    </div>
  );
}

const shareDef = (m: Member): MenuDef => ({
  id: `share-${m.id}`,
  title: `Share ${m.name}'s profile`,
  Content: () => <ShareCard m={m} />,
  /* stacked on desktop → the wide tall card; mobile stays a bottom
     pullup */
  subSize: "lg",
  mobileCard: true,
  bare: true,
  bottomCard: true,
});

/* the client's own profile swaps message/friend for a big Open
   Settings button; share stays — your profile is shareable too.
   settings comes from the mdx directly to dodge a menus.tsx import
   cycle (menus → ProfileHero → this file) */
const selfSettingsDef: MenuDef = {
  id: "settings",
  ...settingsMenu,
  Content: SettingsContent,
};

/* the 3-button action row — Message, friend toggle (add/remove, both
   confirmed), Share (pops the stacked share card); on your own profile
   it's Open Settings + Share */
function ProfileActions({ m }: { m: Member }) {
  const router = useRouter();
  const menu = useMenu();
  const square =
    "grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-raise text-fg-dim transition hover:bg-lift hover:text-fg";
  if (m.self) {
    return (
      <div className="mt-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => menu.popup(selfSettingsDef)}
          className="flex h-9 min-w-0 flex-1 items-center justify-center gap-2 rounded-lg bg-accent px-3 text-note font-semibold text-fg transition hover:brightness-110"
        >
          <GearIcon className="h-4 w-4" />
          Open Settings
        </button>
        <button
          type="button"
          aria-label={`Share ${m.name}'s profile`}
          onClick={() => menu.popup(shareDef(m))}
          className={square}
        >
          <ShareIcon className="h-4.5 w-4.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-center gap-2">
      <button
        type="button"
        onClick={() => router.push(`/app/d/${m.id}`)}
        className="flex h-9 min-w-0 flex-1 items-center justify-center gap-2 rounded-lg bg-accent px-3 text-note font-semibold text-fg transition hover:brightness-110"
      >
        <MessageIcon className="h-4 w-4" />
        Message
      </button>
      <button
        type="button"
        aria-label={m.friended ? `Remove ${m.name}` : `Add ${m.name}`}
        onClick={() =>
          m.friended
            ? menu.confirm({
                title: "Are you sure?",
                body: `Remove ${m.name} as a friend?`,
                danger: true,
                actions: [
                  { label: "Remove friend", tone: "accent" },
                  { label: "Cancel", tone: "subtle" },
                ],
              })
            : menu.confirm({
                title: "Are you sure?",
                body: `Send ${m.name} a friend request?`,
                actions: [
                  { label: "Send request", tone: "accent" },
                  { label: "Cancel", tone: "subtle" },
                ],
              })
        }
        className={square}
      >
        {m.friended ? (
          <UserCheckIcon className="h-4.5 w-4.5" />
        ) : (
          <UserPlusIcon className="h-4.5 w-4.5" />
        )}
      </button>
      <button
        type="button"
        aria-label={`Share ${m.name}'s profile`}
        onClick={() => menu.popup(shareDef(m))}
        className={square}
      >
        <ShareIcon className="h-4.5 w-4.5" />
      </button>
    </div>
  );
}

/* the lg popup's left side — banner, punched pfp, display name, the
   copy-handle + badge chip, the 3 actions, joined, open-text bio, the
   note row and friends-since line, then mutual stacks */
function ProfileSide({ m }: { m: Member }) {
  const { mobile } = useMenu();
  const copyLine = useCopyLine();
  return (
    <div
      className={`flex min-h-full min-w-0 flex-1 flex-col ${
        mobile ? "w-full" : "border-r border-line"
      }`}
    >
      <div
        aria-hidden
        className={`-ml-1.5 -mt-1.5 shrink-0 rounded-tl-lg ${
          mobile ? "h-24" : "h-36"
        }`}
        style={{
          background:
            "linear-gradient(115deg, var(--color-accent) 0%, var(--color-work) 100%)",
        }}
      />
      <div className="-mt-12 ml-5 w-fit rounded-2xl bg-slab p-1.5">
        <span className="relative block">
          <Pfp src={m.src} status={m.status} size={96} rounded="rounded-2xl" />
        </span>
      </div>
      {/* scrolls inside the fixed-height card */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto scroll-dark px-5 pb-4">
        <div className="mt-3 flex flex-col">
          <button
            type="button"
            onClick={copyLine(m.name)}
            className="w-fit truncate rounded-sm text-[22px] font-bold text-fg transition hover:text-accent-2"
          >
            {m.name}
          </button>
          <div className="mt-1 flex items-center gap-1.5">
            <CopyHandle m={m} />
            <span className="text-title text-fg-faint">·</span>
            <span className="flex items-center gap-2 rounded-md bg-ink/70 px-2.5 py-1.5 ring-1 ring-line">
              <MemberBadges m={m} size={22} />
              {!(m.badges ?? []).length ? (
                <span className="text-micro text-fg-faint">no badges</span>
              ) : null}
            </span>
          </div>
        </div>
        <ProfileActions m={m} />
        <div className="mt-5">
          <ProfileBio m={m} />
        </div>
        {/* label / value pairs */}
        <p className="mt-5 text-note font-semibold text-fg-mute">
          Member Since
        </p>
        <p className="mt-0.5 text-note text-fg">{m.joined}</p>
        {m.friendsSince ? (
          <>
            <p className="mt-5 text-note font-semibold text-fg-mute">
              Friends Since
            </p>
            <p className="mt-0.5 text-note text-fg">{m.friendsSince}</p>
          </>
        ) : null}
        <ProfileNote m={m} />
        <StatsNerds m={m} />
      </div>
    </div>
  );
}

/* the right side — word tabs up top with an underline strip; they
   switch visually but carry no content yet */
const PROFILE_TABS = ["Posts", "Activity", "Mutuals", "About"];

function ProfileTabs() {
  const [tab, setTab] = useState(0);
  return (
    <div className="flex min-w-0 flex-1 flex-col">
      <div className="flex gap-6 border-b border-line px-5 pt-3">
        {PROFILE_TABS.map((t, i) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(i)}
            className={`relative pb-2 text-note font-semibold transition after:absolute after:-bottom-px after:left-0 after:h-0.5 after:w-full after:rounded-full after:bg-accent after:transition-opacity ${
              tab === i
                ? "text-fg after:opacity-100"
                : "text-fg-faint after:opacity-0 hover:text-fg-mute"
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <div className="flex-1" />
    </div>
  );
}

/* "Stats for nerds" — bottom of the left pane: the favicon + wordmark
   on the left, the machine bits on the right — a fake 18-digit
   snowflake uuid, the username, and the join timestamp */
function StatsNerds({ m }: { m: Member }) {
  const uuid = memberUuid(m);
  /* "Jan 4, 2021" → "2021-01-04 00:00:00 UTC" */
  const [mon, day, year] = m.joined.replace(",", "").split(" ");
  const mm = String(
    "JanFebMarAprMayJunJulAugSepOctNovDec".indexOf(mon) / 3 + 1,
  ).padStart(2, "0");
  const dd = day.padStart(2, "0");
  return (
    <div className="mt-auto pt-5">
      <p className="text-note font-semibold text-fg-mute">
        Stats for nerds
      </p>
      <div className="mt-2 flex items-stretch gap-4">
        {/* the see-through wordmark scales to fit — never taller than
            the stats text, ≥10px gap before it */}
        {/* eslint-disable-next-line @next/next/no-img-element -- logo asset */}
        <img
          src="/heading1.png"
          alt="Isolation"
          className="min-w-0 max-h-12 flex-1 self-center object-contain object-left opacity-80"
        />
        <div className="shrink-0 font-mono text-[10px] leading-4 text-fg-faint">
          <p className="truncate">uuid: {uuid}:{m.tag}</p>
          <p className="truncate">name: {m.name};{m.id}</p>
          <p className="truncate">joined: {year}-{mm}-{dd} 00:00:00 UTC</p>
        </div>
      </div>
    </div>
  );
}

function ProfileMid({ m }: { m: Member }) {
  const { mobile } = useMenu();
  return (
    /* mobile fills the fullscreen slide-up sheet's body; desktop fills
       the lg popup's fixed frame */
    <div className="flex h-full min-h-full w-full">
      <ProfileSide m={m} />
      {mobile ? null : <ProfileTabs />}
    </div>
  );
}

export const profileDef = (m: Member): MenuDef => ({
  id: `profile-${m.id}`,
  title: `${m.name}'s profile`,
  tab: "Profile",
  Content: () => <ProfileMid m={m} />,
  /* desktop: centered two-pane popup (profile | info); mobile is the
     fullscreen slide-up sheet showing just the profile side */
  popupSize: "lg",
  bare: true,
});

/* member options dropdown — the ⋮ on the right of each row */
export const memberOpts = (m: Member): MenuDef => ({
  id: `member-opts-${m.id}`,
  title: m.name,
  tab: "Member",
  Content: function MemberOpts() {
    const menu = useMenu();
    return (
      <>
        <MenuItem icon="users" onClick={() => menu.popup(profileDef(m))}>
          View profile
        </MenuItem>
        <MenuItem icon="send">Message</MenuItem>
        <MenuItem icon="flag" onClick={() => menu.popup(reportDef("user"))}>
          Report
        </MenuItem>
        <MenuItem icon="x">Remove from GC</MenuItem>
      </>
    );
  },
});

/* friend options — same dropdown, different third row */
export const friendOpts = (m: Member): MenuDef => ({
  id: `friend-opts-${m.id}`,
  title: m.name,
  tab: "Friend",
  Content: function FriendOpts() {
    const menu = useMenu();
    return (
      <>
        <MenuItem icon="users" onClick={() => menu.popup(profileDef(m))}>
          View profile
        </MenuItem>
        <MenuItem icon="send">Message</MenuItem>
        <MenuItem icon="flag" onClick={() => menu.popup(reportDef("user"))}>
          Report
        </MenuItem>
        <MenuItem icon="x">Remove friend</MenuItem>
      </>
    );
  },
});

/* overlapping mini-pfp stack — shows up to 3 faces; past 3 it's the
   first 3 minus one plus a "+N" tile in the same square. display only —
   the row's text label carries the press action */
function FaceStack({ items }: { items: { src: string; label: string }[] }) {
  const extra = items.length - 3;
  const shown = extra > 0 ? items.slice(0, 3) : items;
  return (
    <span className="flex shrink-0 -space-x-1.5">
      {shown.map((s) => (
        // eslint-disable-next-line @next/next/no-img-element -- favicon-sized tiles
        <img
          key={s.src + s.label}
          src={s.src}
          alt={s.label}
          onPointerEnter={(e) => showTip(s.label, e.currentTarget, "top")}
          onPointerLeave={hideTip}
          className="h-[18px] w-[18px] rounded-md object-cover ring-1 ring-slab"
        />
      ))}
      {extra > 0 ? (
        <span className="grid h-[18px] w-[18px] place-items-center rounded-md bg-ink text-[8px] font-bold text-fg-faint ring-1 ring-slab">
          +{extra}
        </span>
      ) : null}
    </span>
  );
}

/* shared server dialog — popped from a mutual-server face */
export const serverDef = (name: string, src: string): MenuDef => ({
  id: `server-${name}`,
  title: name,
  tab: "Server",
  Content: () => (
    <div className="flex w-56 flex-col items-center gap-2.5 p-6">
      {/* eslint-disable-next-line @next/next/no-img-element -- server icon */}
      <img src={src} alt="" className="h-16 w-16 rounded-2xl" />
      <p className="text-heading font-bold text-fg">{name}</p>
      <p className="text-micro text-fg-faint">
        Shared server — full page coming soon
      </p>
    </div>
  ),
  mobileCard: true,
  bare: true,
  bottomCard: true,
});

/* the quick-info card — same hero anatomy as the profile popup:
   banner, punched-out pfp with status ring, display name (opens the
   full dialog on desktop; copies the handle on mobile), then
   username · badges with the slide-open copy chip. bio / mutual
   stacks / friend action below. desktop renders it as the side
   pullout wing — mobile skips it entirely (member rows go straight
   to the big profile) */
function QuickCard({
  m,
  onEscalate,
}: {
  m: Member;
  /* fired when the card hands off to a full popup — the desktop wing
     uses it to close itself */
  onEscalate?: () => void;
}) {
  const { popup, mobile } = useMenu();
  const copyLine = useCopyLine();
  const handle = `${m.id}#${m.tag}`;
  const openProfile = () => {
    popup(profileDef(m));
    onEscalate?.();
  };
  return (
    <div className="flex flex-col">
      {/* banner — same accent wash as the profile hero */}
      <div
        aria-hidden
        className="h-20 shrink-0 rounded-tl-xl"
        style={{
          background:
            "linear-gradient(115deg, var(--color-accent) 0%, var(--color-work) 100%)",
        }}
      />
      <div className="px-3">
        <div className="-mt-8">
          <span className="relative inline-block rounded-2xl bg-slab ring-4 ring-slab">
            <Pfp
              status={m.status}
              size={64}
              rounded="rounded-xl"
              src={m.src}
            />
            <RankMark m={m} />
          </span>
        </div>
        {/* display name — opens the full profile dialog on desktop;
            on mobile it just copies the handle */}
        <button
          type="button"
          onClick={mobile ? copyLine(handle) : openProfile}
          className="mt-2 block max-w-full truncate rounded-sm text-heading font-bold text-fg transition hover:text-accent-2"
        >
          {m.name}
        </button>
        {/* username · badges — tap the handle to copy; hovering slides
            the copy chip open inline, same as the profile hero */}
        <span className="mt-1 flex w-fit items-center gap-1.5">
          <CopyHandle m={m} />
          {(m.badges ?? []).some((b) => DISPLAY_BADGES.includes(b)) ? (
            <>
              <span className="text-title text-fg-faint">·</span>
              {/* quick-info badges ride bare — no holder chip */}
              <MemberBadges m={m} size={17} />
            </>
          ) : null}
        </span>
      </div>
      <div className="flex flex-col gap-2.5 p-4 pt-3">
        <ProfileBio m={m} onPress={openProfile} />
        <p className="text-micro text-fg-faint">Joined {m.joined}</p>
        <ProfileMutuals m={m} onTextPress={openProfile} />
      </div>
      {/* sticky so an overflowing card pins it to the card's bottom
          edge instead of clipping it under the scroll fold */}
      <div className="sticky bottom-0 mt-2">
        <FriendAction m={m} />
      </div>
    </div>
  );
}

/* member row — [pfp+status ring] [name badges - GC badge] / [id#tag];
   click pops the quick card (desktop) / big profile (mobile), right-click
   opens the options menu at the cursor */
function MemberRow({
  m,
  onPick,
  friend = false,
}: {
  m: Member;
  onPick: (m: Member, el: HTMLElement) => void;
  /* friend rows hide the GC rank icon and use the friend menu */
  friend?: boolean;
}) {
  const { openAt } = useMenu();
  const rank = !friend && m.rank ? RANK[m.rank] : null;
  return (
    <li
      className="group/row flex items-center rounded-lg transition hover:bg-raise"
      onContextMenu={(e) => {
        e.preventDefault();
        openAt(friend ? friendOpts(m) : memberOpts(m), e.clientX, e.clientY);
      }}
    >
      <button
        type="button"
        onClick={(e) => onPick(m, e.currentTarget)}
        className="flex min-w-0 flex-1 items-center gap-2.5 px-2.5 py-1.5 text-left"
      >
        <span className="relative shrink-0">
          <Pfp src={m.src} status={m.status} size={32} rounded="rounded-full" />
          {rank && m.rank === "owner" ? (
            <span
              className="absolute -right-1.5 -top-1.5"
              onPointerEnter={(e) => showTip(rank.tip, e.currentTarget, "top")}
              onPointerLeave={hideTip}
            >
              <CrownIcon
                aria-label={rank.tip}
                className="h-3.5 w-3.5 rotate-[35deg] text-idle drop-shadow-[0_1px_1px_rgb(0_0_0/0.7)]"
              />
            </span>
          ) : null}
        </span>
        <span className="flex min-w-0 flex-col">
          <span className="flex items-center gap-1.5">
            <span className="truncate text-note font-semibold text-fg">
              {m.name}
            </span>
            <MemberBadges m={m} />
          </span>
          <span className="truncate text-micro leading-tight text-fg-faint">
            {m.id}#{m.tag}
          </span>
        </span>
      </button>
    </li>
  );
}

/* dm details body — a copy of the profile popup's left pane shrunk to
   the sidebar: banner, punched pfp, name, handle + badges, an action
   row (Open profile instead of Message — this IS the dm), bio, dates,
   note, stats for nerds */
function DmProfile({ m }: { m: Member }) {
  const menu = useMenu();
  const square =
    "grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-raise text-fg-dim transition hover:bg-lift hover:text-fg";
  return (
    <div className="flex min-h-full flex-col">
      <div
        aria-hidden
        className="h-20 shrink-0"
        style={{
          background:
            "linear-gradient(115deg, var(--color-accent) 0%, var(--color-work) 100%)",
        }}
      />
      <div className="-mt-8 ml-4 w-fit rounded-xl bg-slab p-1">
        <Pfp src={m.src} status={m.status} size={64} rounded="rounded-lg" />
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
        <div className="mt-2 flex flex-col">
          {/* display name escalates to the full profile popup — the
              handle below still copies */}
          <button
            type="button"
            onClick={() => menu.popup(profileDef(m))}
            className="w-fit truncate rounded-sm text-heading font-bold text-fg transition hover:text-accent-2"
          >
            {m.name}
          </button>
          <div className="mt-1 flex items-center gap-1.5">
            <CopyHandle m={m} />
            {(m.badges ?? []).some((b) => DISPLAY_BADGES.includes(b)) ? (
              <>
                <span className="text-title text-fg-faint">·</span>
                <span className="flex items-center gap-1.5 rounded-md bg-ink/70 px-2 py-1 ring-1 ring-line">
                  <MemberBadges m={m} size={14} />
                </span>
              </>
            ) : null}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => menu.popup(profileDef(m))}
            className="flex h-9 min-w-0 flex-1 items-center justify-center gap-2 rounded-lg bg-accent px-3 text-note font-semibold text-fg transition hover:brightness-110"
          >
            <UsersIcon className="h-4 w-4" />
            Open profile
          </button>
          <button
            type="button"
            aria-label={m.friended ? `Remove ${m.name}` : `Add ${m.name}`}
            onClick={() =>
              m.friended
                ? menu.confirm({
                    title: "Are you sure?",
                    body: `Remove ${m.name} as a friend?`,
                    danger: true,
                    actions: [
                      { label: "Remove friend", tone: "accent" },
                      { label: "Cancel", tone: "subtle" },
                    ],
                  })
                : menu.confirm({
                    title: "Are you sure?",
                    body: `Send ${m.name} a friend request?`,
                    actions: [
                      { label: "Send request", tone: "accent" },
                      { label: "Cancel", tone: "subtle" },
                    ],
                  })
            }
            className={square}
          >
            {m.friended ? (
              <UserCheckIcon className="h-4.5 w-4.5" />
            ) : (
              <UserPlusIcon className="h-4.5 w-4.5" />
            )}
          </button>
          <button
            type="button"
            aria-label={`Share ${m.name}'s profile`}
            onClick={() => menu.popup(shareDef(m))}
            className={square}
          >
            <ShareIcon className="h-4.5 w-4.5" />
          </button>
        </div>
        <div className="mt-5">
          <ProfileBio m={m} />
        </div>
        <p className="mt-5 text-note font-semibold text-fg-mute">
          Member Since
        </p>
        <p className="mt-0.5 text-note text-fg">{m.joined}</p>
        {m.friendsSince ? (
          <>
            <p className="mt-5 text-note font-semibold text-fg-mute">
              Friends Since
            </p>
            <p className="mt-0.5 text-note text-fg">{m.friendsSince}</p>
          </>
        ) : null}
        <ProfileNote m={m} />
        {/* no stats for nerds here — just the wordmark at the bottom */}
        <div className="mt-auto pt-5">
          {/* eslint-disable-next-line @next/next/no-img-element -- logo asset */}
          <img
            src="/heading1.png"
            alt="Isolation"
            className="max-h-8 object-contain opacity-60"
          />
        </div>
      </div>
    </div>
  );
}

export function DetailsSidebar({
  open,
  collapsed = false,
  onClose,
  pane = false,
  kind = "dm",
}: {
  open: boolean;
  /* xl+ inline mode — squeezes to zero width instead of unmounting */
  collapsed?: boolean;
  onClose: () => void;
  /* mobile — lives as page 2 of the pager; the pager owns the slide
     physics, so the pane itself has no drag or translate */
  pane?: boolean;
  kind?: ChatKind;
}) {
  const { popup } = useMenu();
  const pathname = usePathname();
  /* dm sidebars show the other person's profile — resolve the member
     from /app/d/<id> */
  const dmMember =
    kind === "dm"
      ? MEMBERS.find((x) => x.id === pathname.split("/")[3])
      : undefined;
  /* member row press — desktop: the quick card pulls out as a wing
     docked flush to the sidebar's left edge, aligned to the row;
     mobile: skips the quick card and opens the big profile popup */
  /* sidebar tabs — Info is the per-kind body below, Search the query
     panel; Logs + Pins are placeholders for now */
  const [tab, setTab] = useState<"info" | "search" | "logs" | "pins">(
    "info",
  );
  const [lastPath, setLastPath] = useState(pathname);
  /* switching conversations lands on Info — reset during render so the
     flip doesn't cascade a second pass */
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setTab("info");
  }
  const [picked, setPicked] = useState<Member | null>(null);
  const [wing, setWing] = useState<{ top: number; right: number }>();
  const pick = (m: Member, el: HTMLElement) => {
    if (pane) {
      popup(profileDef(m));
      return;
    }
    setPicked(m);
    const row = el.getBoundingClientRect();
    const aside = el.closest("aside")?.getBoundingClientRect();
    if (aside)
      setWing({
        top: Math.max(8, row.top),
        right: window.innerWidth - aside.left,
      });
  };
  /* clamp against the card's real height — bios/mutuals make it vary,
     so a fixed estimate let it clip off the page's bottom edge. Runs
     before paint so the push-up never flickers */
  const wingEl = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = wingEl.current;
    if (!picked || !wing || !el) return;
    const top = Math.max(8, Math.min(wing.top, window.innerHeight - el.offsetHeight - 8));
    if (top !== wing.top) setWing({ ...wing, top });
  }, [picked, wing]);

  return (
    <>
    <aside
      aria-label="Details"
      className={
        pane
          /* mobile — page 2 of the pager; the pager slides it in from
             the right, the pane itself just fills the page */
          ? "relative flex h-full w-full touch-pan-y flex-col overflow-x-hidden overflow-y-auto bg-slab text-fg scroll-dark"
          : `relative flex w-0 shrink-0 flex-col overflow-x-hidden overflow-y-auto border-l border-line bg-slab text-fg shadow-[inset_10px_0_14px_-10px_rgb(0_0_0/0.4)] transition-[width,translate] duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] scroll-dark max-xl:absolute max-xl:inset-y-0 max-xl:right-0 max-xl:z-30 max-xl:w-[86%] max-xl:max-w-[340px] max-xl:shadow-[0_0_40px_rgb(0_0_0/0.5)] ${
              /* under xl it lives offscreen to the right and pulls out */
              open
                ? "max-xl:translate-x-0"
                : "max-xl:translate-x-full max-xl:pointer-events-none"
            } ${
              collapsed ? "xl:w-0 xl:border-l-0" : "xl:w-[var(--side-w)]"
            }`
      }
    >
      {/* word tabs — Info / Search / Logs / Pins spread across the
          sidebar's full width, accent underline on the active one,
          baseline line under the whole row; close pinches the right */}
      <div className="mt-1 flex items-stretch border-b border-line px-5 pt-4">
        <div
          role="tablist"
          aria-label="Details panels"
          className="flex flex-1 items-stretch justify-between gap-2 pr-3"
        >
          {(["info", "search", "logs", "pins"] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`relative px-1 pb-2 text-note font-semibold capitalize transition after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full after:bg-accent after:transition-opacity ${
                tab === t
                  ? "text-fg after:opacity-100"
                  : "text-fg-faint after:opacity-0 hover:text-fg-mute"
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <IconButton
          label="Close details"
          tip="Close"
          onClick={onClose}
          className={`-mt-1 ${pane ? "" : "xl:hidden"}`}
        >
          <XIcon className="h-4.5 w-4.5" />
        </IconButton>
      </div>

      {/* body — notes get the free-form graph; gc/server get the pfp
          honeycomb on top of the member list; dms get the member's
          profile; the search tab swaps the whole body for the query
          panel */}
      <div className="relative flex-1 py-1">
        {tab === "search" ? (
          <div className="absolute inset-0 flex">
            <SearchPanel onClose={() => setTab("info")} />
          </div>
        ) : tab === "logs" || tab === "pins" ? (
          <div className="grid h-full place-items-center">
            <p className="text-micro text-fg-faint">
              Nothing here yet.
            </p>
          </div>
        ) : kind === "notes" ? (
          <NotesList />
        ) : kind === "gc" || kind === "server" ? (
          <>
            {/* honeycomb — staggered 3/4/3 square-pfp cluster; each tile
                opens the user's profile popup (mid card on desktop,
                bottom card on mobile) */}
            <div className="flex flex-col items-center gap-1 px-5 pt-2">
              {HONEY.map((row, i) => (
                <div key={i} className="flex gap-1">
                  {row.map((m) => (
                    <button
                      key={m.src}
                      type="button"
                      aria-label={`Open ${m.name}'s profile`}
                      onClick={() => popup(profileDef(m))}
                      className="relative rounded-xl ring-2 ring-slab transition hover:scale-105 hover:ring-accent/60"
                    >
                      <Pfp
                        src={m.src}
                        status={m.status}
                        size={44}
                        rounded="rounded-xl"
                      />
                      {m.rank === "owner" ? (
                        <CrownIcon
                          aria-label={RANK.owner?.tip}
                          className="absolute -right-1.5 -top-1.5 h-4 w-4 rotate-[35deg] text-idle drop-shadow-[0_1px_1px_rgb(0_0_0/0.7)]"
                        />
                      ) : null}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <SectionLabel>Members - {MEMBERS.length}/25</SectionLabel>
            <ul className="space-y-0.5 px-2 pb-4">
              {MEMBERS.map((m) => (
                <MemberRow key={m.src} m={m} onPick={pick} />
              ))}
            </ul>
          </>
        ) : kind === "dm" ? (
          dmMember ? <DmProfile m={dmMember} /> : null
        ) : kind === "friends" ? (
          <>
            {/* the friends table lives in the sidebar now — same row
                anatomy as the GC list, minus the rank icons */}
            <SectionLabel>Friends - {MEMBERS.length}</SectionLabel>
            <ul className="space-y-0.5 px-2 pb-4">
              {MEMBERS.map((m) => (
                <MemberRow key={m.src} m={m} onPick={pick} friend />
              ))}
            </ul>
          </>
        ) : null}
      </div>

    </aside>
    {/* desktop side pullout — a wing docked flush to the sidebar's left
        edge, aligned to the clicked row; the aside clips x-overflow so
        this lives OUTSIDE it as a fixed sibling. backdrop click closes */}
    {picked && !pane && wing ? (
      <>
        <div
          aria-hidden
          className="fixed inset-0 z-40"
          onPointerDown={() => setPicked(null)}
        />
        <div
          ref={wingEl}
          role="dialog"
          aria-label={`${picked.name} quick info`}
          className="fixed z-50 w-72 animate-pullr overflow-y-auto rounded-l-xl rounded-r-none bg-slab ring-1 ring-line shadow-[0_18px_48px_rgb(0_0_0/0.55)] scroll-dark"
          style={{
            top: wing.top,
            right: wing.right,
            maxHeight: "calc(100vh - 16px)",
          }}
        >
          <QuickCard m={picked} onEscalate={() => setPicked(null)} />
        </div>
      </>
    ) : null}
    </>
  );
}
